import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CostInputs } from '@/types/businesscase';
import { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CostsInputCardProps {
  costs: CostInputs;
  onUpdate: (costs: CostInputs) => Promise<void>;
}

export function CostsInputCard({ costs, onUpdate }: CostsInputCardProps) {
  const [localCosts, setLocalCosts] = useState(costs);
  const [saving, setSaving] = useState(false);

  // Debounced save
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (JSON.stringify(localCosts) !== JSON.stringify(costs)) {
        setSaving(true);
        try {
          await onUpdate(localCosts);
        } finally {
          setSaving(false);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [localCosts]);

  const handleChange = (field: keyof CostInputs, value: string) => {
    const numValue = parseFloat(value) || 0;
    setLocalCosts(prev => ({ ...prev, [field]: numValue }));
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Kosten Invoer
          {saving && <span className="text-xs text-muted-foreground">Opslaan...</span>}
        </CardTitle>
        <CardDescription>Vul de investeringskosten in voor dit project</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="battery-cost">Batterij Aanschaf (€)</Label>
          <Input
            id="battery-cost"
            type="number"
            value={localCosts.batteryPurchaseCost}
            onChange={(e) => handleChange('batteryPurchaseCost', e.target.value)}
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            {formatNumber(localCosts.batteryPurchaseCost)}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="installation-cost">Installatie Kosten (€)</Label>
          <Input
            id="installation-cost"
            type="number"
            value={localCosts.installationCost}
            onChange={(e) => handleChange('installationCost', e.target.value)}
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            {formatNumber(localCosts.installationCost)}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="software-cost">Software Setup (€)</Label>
          <Input
            id="software-cost"
            type="number"
            value={localCosts.softwareSetupCost}
            onChange={(e) => handleChange('softwareSetupCost', e.target.value)}
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            {formatNumber(localCosts.softwareSetupCost)}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="subscription">Jaarlijks Abonnement (€)</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">Wordt in jaar 0 meebetaald bij CAPEX</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Latere jaren zitten in "EMS Costs" per jaar
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Input
            id="subscription"
            type="number"
            value={localCosts.annualSubscription}
            onChange={(e) => handleChange('annualSubscription', e.target.value)}
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            {formatNumber(localCosts.annualSubscription)} per jaar
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="other-costs">Overige Kosten (€)</Label>
          <Input
            id="other-costs"
            type="number"
            value={localCosts.otherCosts}
            onChange={(e) => handleChange('otherCosts', e.target.value)}
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            {formatNumber(localCosts.otherCosts)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
